import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const VACIO = { emoji: "", categoria: "", subcategoria: "", tipo: "si_no", descripcion: "", a_qn: "supervisora", activo: true, opcional: false, respuesta_multiple: false };

const TIPO_LABEL = { semaforo: "🚦 Semáforo", "semaforo+desconozco": "🚦❓ Semáforo+Desconozco", si_no: "✅ Sí/No", texto: "📝 Texto", opciones_color: "🎨 Opciones con color", seleccion_residentes: "👥 Selección de residentes", estrellas: "⭐ Estrellas (1-5)" };
const TIPO_BG = { semaforo: "#dcfce7", "semaforo+desconozco": "#ede9fe", si_no: "#dbeafe", texto: "#fef9c3", opciones_color: "#ffe4e6", seleccion_residentes: "#f3e8ff", estrellas: "#fef3c7" };
const TIPO_COLOR = { semaforo: "#166534", "semaforo+desconozco": "#5b21b6", si_no: "#1e40af", texto: "#854d0e", opciones_color: "#9f1239", seleccion_residentes: "#6b21a8", estrellas: "#b45309" };

const A_QN_TABS = [
  { val: "supervisora", label: "👩‍💼 Supervisora" },
  { val: "asistenta", label: "🧹 Asistenta" },
  { val: "residente", label: "🧑‍🎓 Residentes" },
  { val: "sustitucion", label: "🔁 Sustituciones" },
];

const COLORES_SUGERIDOS = ["#16a34a", "#d97706", "#dc2626", "#3b82f6", "#7c3aed", "#94a3b8"];

// Colores según cuántas opciones haya (de mejor a peor: verde → rojo)
const PALETAS_OPCIONES = {
  2: ["#16a34a", "#dc2626"],
  3: ["#16a34a", "#f59e0b", "#dc2626"],
  4: ["#16a34a", "#84cc16", "#f97316", "#dc2626"],
  5: ["#16a34a", "#84cc16", "#facc15", "#f97316", "#dc2626"],
  6: ["#15803d", "#22c55e", "#84cc16", "#facc15", "#f97316", "#dc2626"],
};

const AdminReporteOpciones = () => {
  const navigate = useNavigate();
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  // Pestaña activa: qué preguntas se listan/crean (a_qn)
  const [aQn, setAQn] = useState("supervisora");
  // Valores personalizados (tipo 'opciones_color'): {id?, etiqueta, color}
  const [valoresForm, setValoresForm] = useState([]);
  const [valoresOriginales, setValoresOriginales] = useState([]);
  // Piloto temporal del reporte de asistentas (true/false)
  const [modoAsistenta, setModoAsistenta] = useState("servicio");   // servicio | reporte | ambos
  const [guardandoFlag, setGuardandoFlag] = useState(false);

  // Modo reordenación
  const [modoOrden, setModoOrden] = useState(false);
  const [ordenLocal, setOrdenLocal] = useState([]); // copia local para drag
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  const [ordenGuardado, setOrdenGuardado] = useState(false);
  const dragIdx = useRef(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/servicio/reporte_opciones?a_qn=${aQn}`);
      setFilas(res.data || []);
      setError("");
    } catch {
      setError("❌ Error al cargar opciones.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [aQn]);

  useEffect(() => {
    axios.get("/api/servicio/config/reporte_asistenta")
      .then((res) => setModoAsistenta(res.data?.modo || (res.data?.activo ? "reporte" : "servicio")))
      .catch(() => {});
  }, []);

  const cambiarModoAsistenta = async (nuevo) => {
    if (nuevo === modoAsistenta) return;
    setGuardandoFlag(true);
    try {
      await axios.put("/api/servicio/config/reporte_asistenta", { modo: nuevo });
      setModoAsistenta(nuevo);
    } catch {
      setError("❌ Error al guardar la configuración.");
    } finally {
      setGuardandoFlag(false);
    }
  };

  // ── CRUD ──
  const abrirNuevo = () => { setForm({ ...VACIO, a_qn: aQn }); setValoresForm([]); setValoresOriginales([]); setEditando("nuevo"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const abrirEditar = (fila) => {
    setForm({ emoji: fila.emoji || "", categoria: fila.categoria, subcategoria: fila.subcategoria, tipo: fila.tipo || "si_no", descripcion: fila.descripcion || "", a_qn: fila.a_qn || "supervisora", activo: !!fila.activo, opcional: !!fila.opcional, respuesta_multiple: !!fila.respuesta_multiple });
    const valores = (fila.valores || []).map((v) => ({ ...v }));
    setValoresForm(valores);
    setValoresOriginales(valores.map((v) => v.id));
    setEditando(fila.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelar = () => { setEditando(null); setForm(VACIO); setValoresForm([]); setValoresOriginales([]); };

  const guardar = async () => {
    if (!form.categoria.trim() || !form.subcategoria.trim()) { setError("Categoría y subcategoría son obligatorias."); return; }
    if (form.tipo === "opciones_color" && valoresForm.filter((v) => v.etiqueta.trim()).length === 0) {
      setError("Añade al menos una opción de respuesta."); return;
    }
    setGuardando(true); setError("");
    try {
      let idPregunta = editando;
      if (editando === "nuevo") {
        const res = await axios.post("/api/servicio/reporte_opciones", form);
        idPregunta = res.data.id;
      } else {
        await axios.put(`/api/servicio/reporte_opciones/${editando}`, form);
      }
      if (form.tipo === "opciones_color") {
        const idsActuales = valoresForm.filter((v) => v.id).map((v) => v.id);
        const eliminadas = valoresOriginales.filter((id) => !idsActuales.includes(id));
        await Promise.all(eliminadas.map((id) => axios.delete(`/api/servicio/reporte_opciones/valores/${id}`)));
        // Se guardan en orden y al final se fija ese orden (orden = posición en la lista)
        const idsEnOrden = [];
        for (const v of valoresForm.filter((x) => x.etiqueta.trim())) {
          if (v.id) {
            await axios.put(`/api/servicio/reporte_opciones/valores/${v.id}`, { etiqueta: v.etiqueta, color: v.color });
            idsEnOrden.push(v.id);
          } else {
            const res = await axios.post(`/api/servicio/reporte_opciones/${idPregunta}/valores`, { etiqueta: v.etiqueta, color: v.color });
            idsEnOrden.push(res.data.id);
          }
        }
        if (idsEnOrden.length) await axios.patch(`/api/servicio/reporte_opciones/${idPregunta}/valores/orden`, { ids: idsEnOrden });
      }
      await cargar(); cancelar();
    } catch (e) { setError(e.response?.data?.error || "❌ Error al guardar."); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta opción?")) return;
    try { await axios.delete(`/api/servicio/reporte_opciones/${id}`); setFilas((f) => f.filter((r) => r.id !== id)); }
    catch { setError("❌ Error al eliminar."); }
  };

  const toggleActivo = async (fila) => {
    const nuevo = !fila.activo;
    setFilas((prev) => prev.map((f) => (f.id === fila.id ? { ...f, activo: nuevo } : f)));
    try {
      await axios.patch(`/api/servicio/reporte_opciones/${fila.id}/activo`, { activo: nuevo });
    } catch {
      setFilas((prev) => prev.map((f) => (f.id === fila.id ? { ...f, activo: !nuevo } : f)));
      setError("❌ Error al actualizar el estado.");
    }
  };

  const agregarValor = () => setValoresForm((prev) => [...prev, { _k: `n${Date.now()}${Math.random()}`, etiqueta: "", color: COLORES_SUGERIDOS[prev.length % COLORES_SUGERIDOS.length] }]);
  // «¿Cuántas opciones?»: deja exactamente n filas (conserva las primeras ya escritas) y pone los colores de la paleta de n
  const elegirNumOpciones = (n) => {
    const sobran = valoresForm.slice(n).filter((v) => v.etiqueta.trim());
    if (sobran.length && !window.confirm(`Se quitarán ${sobran.length} opción(es) con texto del final de la lista. ¿Seguir?`)) return;
    setValoresForm((prev) => {
      const base = prev.slice(0, n);
      while (base.length < n) base.push({ _k: `n${Date.now()}${Math.random()}`, etiqueta: "", color: "#94a3b8" });
      return base.map((v, i) => ({ ...v, color: PALETAS_OPCIONES[n][i] }));
    });
  };
  const editarValor = (idx, campo, val) => setValoresForm((prev) => prev.map((v, i) => (i === idx ? { ...v, [campo]: val } : v)));
  const quitarValor = (idx) => setValoresForm((prev) => prev.filter((_, i) => i !== idx));
  // Arrastrar una respuesta (por el asa ⠿) para cambiar su posición: el orden de la
  // lista es el que se guarda (columna "orden") y el que se ve en el reporte.
  const [valorArrastrable, setValorArrastrable] = useState(null); // idx cuyo asa está pulsada
  const [valorArrastrando, setValorArrastrando] = useState(null);
  const moverValorA = (desde, hasta) => setValoresForm((prev) => {
    if (desde === hasta || desde == null || hasta == null) return prev;
    const copia = [...prev];
    const [m] = copia.splice(desde, 1);
    copia.splice(hasta, 0, m);
    return copia;
  });
  const onValorDragOver = (e, idx) => {
    e.preventDefault();
    if (valorArrastrando === null || valorArrastrando === idx) return;
    moverValorA(valorArrastrando, idx);
    setValorArrastrando(idx);
  };
  const onValorTouchMove = (e) => {
    if (valorArrastrando === null) return;
    const t = e.touches[0];
    const el = document.elementFromPoint(t.clientX, t.clientY)?.closest("[data-valor-idx]");
    if (!el) return;
    const idx = Number(el.getAttribute("data-valor-idx"));
    if (idx !== valorArrastrando) { moverValorA(valorArrastrando, idx); setValorArrastrando(idx); }
  };
  const finArrastreValor = () => { setValorArrastrando(null); setValorArrastrable(null); };

  // ── REORDENACIÓN ──
  const abrirOrden = () => { setOrdenLocal([...filas]); setModoOrden(true); setOrdenGuardado(false); };
  const cancelarOrden = () => { setModoOrden(false); setOrdenLocal([]); };

  const onDragStart = (e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const arr = [...ordenLocal];
    const [moved] = arr.splice(dragIdx.current, 1);
    arr.splice(idx, 0, moved);
    dragIdx.current = idx;
    setOrdenLocal(arr);
  };
  const onDragEnd = () => { dragIdx.current = null; };

  // Touch drag support
  const touchStartY = useRef(null);
  const touchStartIdx = useRef(null);
  const onTouchStart = (e, idx) => { touchStartY.current = e.touches[0].clientY; touchStartIdx.current = idx; };
  const onTouchMove = (e) => {
    e.preventDefault();
    const y = e.touches[0].clientY;
    const items = document.querySelectorAll("[data-drag-item]");
    let targetIdx = touchStartIdx.current;
    items.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) targetIdx = i;
    });
    if (targetIdx !== touchStartIdx.current) {
      const arr = [...ordenLocal];
      const [moved] = arr.splice(touchStartIdx.current, 1);
      arr.splice(targetIdx, 0, moved);
      touchStartIdx.current = targetIdx;
      setOrdenLocal(arr);
    }
  };
  const onTouchEnd = () => { touchStartY.current = null; touchStartIdx.current = null; };

  const guardarOrden = async () => {
    setGuardandoOrden(true);
    try {
      const payload = ordenLocal.map((f, i) => ({ id: f.id, orden: i + 1 }));
      await axios.patch("/api/servicio/reporte_opciones/reordenar", payload);
      setFilas(ordenLocal.map((f, i) => ({ ...f, orden: i + 1 })));
      setOrdenGuardado(true);
      setTimeout(() => { setModoOrden(false); setOrdenLocal([]); setOrdenGuardado(false); }, 1000);
    } catch { setError("❌ Error al guardar el orden."); }
    finally { setGuardandoOrden(false); }
  };

  const categorias = [...new Set(filas.map((f) => f.categoria))];

  // ══ MODO REORDENACIÓN ══
  if (modoOrden) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0f4f8", padding: "24px 16px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <button onClick={cancelarOrden} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#3b82f6", fontWeight: 700, padding: 0 }}>
              ← Cancelar
            </button>
            <h2 style={{ flex: 1, fontSize: 20, fontWeight: 800, color: "#1e3a5f", margin: 0 }}>📋 Ordenar preguntas</h2>
            <button
              onClick={guardarOrden}
              disabled={guardandoOrden}
              style={{ background: ordenGuardado ? "#16a34a" : "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
            >
              {ordenGuardado ? "✅ Guardado" : guardandoOrden ? "Guardando…" : "💾 Guardar orden"}
            </button>
          </div>

          <div style={{ background: "#fff3cd", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 14, color: "#856404" }}>
            🖱️ Arrastra las filas para cambiar el orden en que aparecen las preguntas en el reporte.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ordenLocal.map((fila, idx) => (
              <div
                key={fila.id}
                data-drag-item
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDragEnd={onDragEnd}
                onTouchStart={(e) => onTouchStart(e, idx)}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "#fff", borderRadius: 10, padding: "12px 16px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
                  cursor: "grab", userSelect: "none",
                  border: "1.5px solid #e2e8f0",
                  transition: "box-shadow 0.1s",
                }}
              >
                {/* Número de orden */}
                <span style={{ minWidth: 28, height: 28, background: "#1e3a5f", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                  {idx + 1}
                </span>
                {/* Handle */}
                <span style={{ fontSize: 20, color: "#94a3b8", flexShrink: 0 }}>⠿</span>
                {/* Emoji */}
                <span style={{ fontSize: 22, minWidth: 28, textAlign: "center" }}>{fila.emoji || ""}</span>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: "#64748b", fontWeight: 500 }}>{fila.categoria}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1e3a5f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fila.subcategoria}</div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", flexShrink: 0 }}>
                  {TIPO_LABEL[fila.tipo] || fila.tipo}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ══ VISTA NORMAL ══
  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", padding: "24px 16px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#3b82f6", fontWeight: 700, marginBottom: 20, padding: 0 }}
        >
          ← Volver
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1e3a5f", margin: 0 }}>📋 Opciones de reporte diario</h2>
          {editando === null && aQn === "residente" && (
            <button onClick={() => navigate("/admin/residentes-plan")} style={{ background: "#ede9fe", color: "#5b21b6", border: "1.5px solid #c4b5fd", borderRadius: 10, padding: "10px 14px", fontWeight: 800, cursor: "pointer", fontSize: 14 }}>🗓️ Reparto de pisos por día</button>
          )}
          {editando === null && (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={abrirOrden}
                style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
              >
                ↕ Ordenar
              </button>
              <button
                onClick={abrirNuevo}
                style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
              >
                + Añadir
              </button>
            </div>
          )}
        </div>

        {editando === null && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {A_QN_TABS.map((t) => (
              <button
                key={t.val}
                onClick={() => setAQn(t.val)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer",
                  border: aQn === t.val ? "2px solid #1e3a5f" : "2px solid #e2e8f0",
                  background: aQn === t.val ? "#1e3a5f" : "#fff",
                  color: aQn === t.val ? "#fff" : "#475569",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {editando === null && aQn === "asistenta" && (
          <div style={{ background: "#fff7ed", border: "1.5px solid #fed7aa", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: "#9a3412", fontSize: 14 }}>¿Qué ve la asistenta al pulsar un piso?</div>
            <div style={{ fontSize: 13, color: "#c2410c", marginBottom: 10 }}>Con «Ambos», cada piso muestra dos botones grandes: servicio y reporte.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["servicio", "🧹 Solo servicio"], ["reporte", "📝 Solo reporte"], ["ambos", "🧹+📝 Ambos"]].map(([k, t]) => (
                <button key={k} onClick={() => cambiarModoAsistenta(k)} disabled={guardandoFlag}
                  style={{ padding: "8px 14px", borderRadius: 20, border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", background: modoAsistenta === k ? "#16a34a" : "#e2e8f0", color: modoAsistenta === k ? "#fff" : "#475569" }}>
                  {modoAsistenta === k ? "✅ " : ""}{t}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <div style={{ background: "#fee2e2", color: "#b91c1c", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>{error}</div>}

        {/* Formulario nuevo / editar */}
        {editando !== null && (
          <div style={{ background: "#fff", border: "2px solid #3b82f6", borderRadius: 14, padding: 20, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e3a5f", marginBottom: 16 }}>
              {editando === "nuevo" ? "Nueva opción" : "Editar opción"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Emoji</label>
                <input type="text" value={form.emoji} onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))} placeholder="Ej: 🧹"
                  style={{ width: 80, padding: "10px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 20, textAlign: "center" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Categoría *</label>
                <input type="text" value={form.categoria} onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))} placeholder="Ej: Limpieza, Lavandería..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Subcategoría *</label>
                <input type="text" value={form.subcategoria} onChange={(e) => setForm((f) => ({ ...f, subcategoria: e.target.value }))} placeholder="Ej: Baños, Habitaciones..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Tipo de respuesta *</label>
                <select value={form.tipo} onChange={(e) => {
                  const nuevoTipo = e.target.value;
                  setForm((f) => ({ ...f, tipo: nuevoTipo, opcional: nuevoTipo === "texto" ? f.opcional : false, respuesta_multiple: (nuevoTipo === "seleccion_residentes" || nuevoTipo === "opciones_color") ? f.respuesta_multiple : false }));
                }}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" }}>
                  <option value="si_no">✅ Sí / No</option>
                  <option value="semaforo">🚦 Semáforo (Verde / Amarillo / Rojo)</option>
                  <option value="semaforo+desconozco">🚦❓ Semáforo + Desconozco</option>
                  <option value="texto">📝 Texto libre</option>
                  <option value="opciones_color">🎨 Opciones con color (a elegir)</option>
                  <option value="seleccion_residentes">👥 Selección de residentes del piso (+ "Ninguno")</option>
                  <option value="estrellas">⭐ Estrellas (1 a 5)</option>
                </select>
              </div>
              {form.tipo === "estrellas" && (
                <div style={{ background: "#fffbeb", borderRadius: 10, padding: 12, border: "1.5px solid #fde68a", fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
                  Se responde pulsando de <strong>1 a 5 estrellas</strong> (1 = muy mal, 5 = muy bien). En los reportes la respuesta se pinta con fondo
                  <strong> rojo → naranja → ámbar → verde</strong> según la puntuación.
                </div>
              )}
              {form.tipo === "seleccion_residentes" && (
                <div style={{ background: "#faf5ff", borderRadius: 10, padding: 12, border: "1.5px solid #e9d5ff", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 13, color: "#6b21a8", lineHeight: 1.5 }}>
                    Las opciones se generan solas al rellenar el reporte: <strong>un botón por cada residente activo del piso</strong> (nombre y habitación) y una opción <strong>«Ninguno»</strong>, que es excluyente.
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: "#581c87", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!form.respuesta_multiple} onChange={(e) => setForm((f) => ({ ...f, respuesta_multiple: e.target.checked }))} />
                    Selección múltiple (se pueden marcar varios residentes)
                  </label>
                </div>
              )}
              {form.tipo === "opciones_color" && (
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, border: "1.5px solid #e2e8f0" }}>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1e3a5f", marginBottom: 6 }}>1. ¿Cuántas opciones tiene la pregunta?</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {[2, 3, 4, 5, 6].map((n) => (
                        <button key={n} type="button" onClick={() => elegirNumOpciones(n)}
                          style={{ minWidth: 48, padding: "10px 0", borderRadius: 12, border: valoresForm.length === n ? "2px solid #2563eb" : "2px solid #e2e8f0", background: valoresForm.length === n ? "#dbeafe" : "#fff", color: "#1e3a5f", fontWeight: 900, fontSize: 18, cursor: "pointer" }}>{n}</button>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Los colores se ponen solos (del verde al rojo) según cuántas sean. Luego puedes cambiarlos.</div>
                  </div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 8 }}>
                    2. Texto de cada opción * <span style={{ fontWeight: 500, color: "#94a3b8" }}>(arrastra ⠿ para ordenarlas: saldrán en este orden en el reporte)</span>
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {valoresForm.map((v, idx) => (
                      <div key={v.id ?? v._k ?? `nuevo-${idx}`} data-valor-idx={idx}
                        draggable={valorArrastrable === idx}
                        onDragStart={(e) => { setValorArrastrando(idx); e.dataTransfer.effectAllowed = "move"; }}
                        onDragOver={(e) => onValorDragOver(e, idx)}
                        onDragEnd={finArrastreValor}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, borderRadius: 10, padding: "4px 6px",
                          background: valorArrastrando === idx ? "#eef2ff" : "transparent",
                          boxShadow: valorArrastrando === idx ? "0 4px 14px rgba(79,70,229,0.25)" : "none",
                          border: valorArrastrando === idx ? "1.5px dashed #818cf8" : "1.5px solid transparent",
                          transition: "background .1s",
                        }}>
                        <span
                          title="Arrastra para ordenar"
                          onMouseDown={() => setValorArrastrable(idx)}
                          onMouseUp={() => setValorArrastrable(null)}
                          onTouchStart={() => { setValorArrastrable(idx); setValorArrastrando(idx); }}
                          onTouchMove={onValorTouchMove}
                          onTouchEnd={finArrastreValor}
                          style={{ cursor: "grab", touchAction: "none", userSelect: "none", fontSize: 20, color: "#94a3b8", padding: "0 4px", lineHeight: 1 }}>⠿</span>
                        <span style={{ minWidth: 18, textAlign: "right", fontSize: 13, fontWeight: 800, color: "#94a3b8" }}>{idx + 1}</span>
                        <input type="color" value={v.color} onChange={(e) => editarValor(idx, "color", e.target.value)}
                          style={{ width: 38, height: 36, border: "1.5px solid #cbd5e1", borderRadius: 6, padding: 0, cursor: "pointer" }} />
                        <input type="text" value={v.etiqueta} onChange={(e) => editarValor(idx, "etiqueta", e.target.value)}
                          placeholder="Ej: Bien hecho"
                          style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} />
                        <button onClick={() => quitarValor(idx)} title="Quitar opción"
                          style={{ background: "#fff1f2", color: "#e11d48", border: "none", borderRadius: 7, padding: "7px 10px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>✕</button>
                      </div>
                    ))}
                  </div>
                  <button onClick={agregarValor}
                    style={{ marginTop: 10, background: "#eff6ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                    + Añadir opción
                  </button>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: "#475569", cursor: "pointer", marginTop: 12 }}>
                    <input type="checkbox" checked={!!form.respuesta_multiple} onChange={(e) => setForm((f) => ({ ...f, respuesta_multiple: e.target.checked }))} />
                    Selección múltiple (se pueden marcar varias opciones)
                  </label>
                </div>
              )}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Descripción (opcional)</label>
                <textarea value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Aclaración que verá el usuario al pulsar ⓘ" rows={3}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 14, boxSizing: "border-box", resize: "vertical" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#475569", cursor: "pointer" }}>
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} />
                Activa (visible en el reporte)
              </label>
              {form.tipo === "texto" && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#475569", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.opcional} onChange={(e) => setForm((f) => ({ ...f, opcional: e.target.checked }))} />
                  Opcional (no hace falta rellenarla para poder enviar el reporte)
                </label>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button onClick={guardar} disabled={guardando}
                style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                {guardando ? "Guardando..." : "Guardar"}
              </button>
              <button onClick={cancelar}
                style={{ background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 8, padding: "10px 16px", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Listado agrupado por categoría (ordenado por orden global) */}
        {loading ? (
          <div style={{ textAlign: "center", color: "#64748b", padding: 40 }}>Cargando...</div>
        ) : filas.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 14, padding: 32, textAlign: "center", color: "#94a3b8" }}>
            No hay opciones todavía. Pulsa "+ Añadir" para crear la primera.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {categorias.map((cat) => (
              <div key={cat} style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <div style={{ background: "#f8fafc", padding: "12px 18px", fontWeight: 700, color: "#1e3a5f", fontSize: 15, borderBottom: "1px solid #e2e8f0" }}>
                  {cat}
                </div>
                {filas.filter((f) => f.categoria === cat).map((fila) => (
                  <div key={fila.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid #f1f5f9", opacity: !fila.activo ? 0.55 : 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ minWidth: 24, height: 24, background: "#e0e7ef", color: "#1e3a5f", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12 }}>
                        {fila.orden}
                      </span>
                      <span style={{ fontSize: 22, minWidth: 30, textAlign: "center" }}>{fila.emoji || ""}</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ fontSize: 15, color: "#334155", fontWeight: 600 }}>{fila.subcategoria}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: TIPO_COLOR[fila.tipo] || "#64748b", background: TIPO_BG[fila.tipo] || "#f1f5f9", borderRadius: 6, padding: "2px 8px", alignSelf: "flex-start" }}>
                          {TIPO_LABEL[fila.tipo] || fila.tipo || "—"}
                        </span>
                        {(fila.tipo === "seleccion_residentes" || fila.tipo === "opciones_color") && !!fila.respuesta_multiple && (
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#6b21a8", background: "#f3e8ff", borderRadius: 6, padding: "2px 8px", alignSelf: "flex-start" }}>
                            Selección múltiple
                          </span>
                        )}
                        {fila.tipo === "texto" && !!fila.opcional && (
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 6, padding: "2px 8px", alignSelf: "flex-start" }}>
                            Opcional
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button onClick={() => toggleActivo(fila)} title={!fila.activo ? "Activar" : "Desactivar"}
                        style={{ background: !fila.activo ? "#f1f5f9" : "#dcfce7", color: !fila.activo ? "#64748b" : "#166534", border: "none", borderRadius: 20, padding: "6px 12px", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
                        {!fila.activo ? "Inactiva" : "✅ Activa"}
                      </button>
                      <button onClick={() => abrirEditar(fila)}
                        style={{ background: "#eff6ff", color: "#2563eb", border: "none", borderRadius: 7, padding: "6px 14px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                        Editar
                      </button>
                      <button onClick={() => eliminar(fila.id)}
                        style={{ background: "#fff1f2", color: "#e11d48", border: "none", borderRadius: 7, padding: "6px 14px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReporteOpciones;
